import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../Components/Catalyst/button';
import { Textarea } from '../../Components/Catalyst/textarea';
import { Text } from '../../Components/Catalyst/text';
import { ErrorAlert } from '../../Components/Custom/ErrorAlert';
import LoadingSpinner from '../../LoadingSpinner';
import { CommentReturn } from '../../clients/rust-api-client';
import { useMember } from '../../Hooks/MemberContext';
import { billingErrorMessage } from '../../utils/billingHelpers';
import { formatRelativeTime, formatTimestamp } from '../../utils/dateUtils';
import { buildCommentTree, CommentNode } from './commentTree';
import MemberAttachmentThumb from './MemberAttachmentThumb';

/*
 * The homeowner's side of a shared thread, whatever it hangs off — a
 * violation (#126) or a reservation (#640 phase 2). Ported from
 * front-porch/src/violation/comments/ViolationCommentThread.tsx.
 *
 * Everything management-only is gone rather than disabled: no tabs, no
 * visibility badges, no `visibility` on the create payload — the member
 * create types have no such field, and the server forces `Shared` — and
 * no file picker, because a homeowner has no upload route yet (#581).
 * Attachments management adds are shown.
 */

export interface MemberThreadSource {
  /** The thread's comments; the server has already scoped them. */
  list: () => Promise<CommentReturn[]>;
  /** Post a top-level message, or a reply to `parentId`. */
  post: (content: string, parentId?: string) => Promise<unknown>;
  /** Delete one of the member's own comments. */
  remove: (commentId: string) => Promise<unknown>;
}

interface Props {
  source: MemberThreadSource;
  /** Changes when the thread is a different one, to reload it. */
  sourceKey: string;
  /** Shown when the thread is empty. */
  emptyText: string;
  /** Label on the send button. */
  sendLabel: string;
  /** How another member's comment is attributed. */
  otherMemberLabel?: string;
  /** Read-only (an impersonated session): the thread shows, nothing posts. */
  readOnlyReason?: string | null;
  title?: string;
}

/**
 * What a homeowner is told about who wrote a management comment.
 *
 * There is no user-lookup endpoint in the member realm, so a manager's
 * name is not obtainable here at all. This matches the wording the
 * notification service already sends for the same event, so the inbox and
 * the thread agree.
 */
export const MANAGEMENT_AUTHOR = 'Your community manager';

export function MemberCommentThread({
  source,
  sourceKey,
  emptyText,
  sendLabel,
  otherMemberLabel = 'A homeowner',
  readOnlyReason = null,
  title = 'Messages',
}: Props) {
  const { member } = useMember();
  const [comments, setComments] = useState<CommentReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadCount, setReloadCount] = useState(0);
  const reload = useCallback(() => setReloadCount((n) => n + 1), []);

  const [content, setContent] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [busy, setBusy] = useState(false);
  const [writeError, setWriteError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    source
      .list()
      .then((list) => {
        if (cancelled) return;
        setComments(list);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading comments:', err);
        // A 404 here means "not yours", and reads as not-found. Never as
        // forbidden, and never with a client-side ownership check that
        // says something different.
        setError(billingErrorMessage(err, "Couldn't load these messages."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // `source` is rebuilt by its parent each render; the key is what
    // says it is a different thread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, reloadCount]);

  const tree = useMemo(() => buildCommentTree(comments), [comments]);
  const readOnly = !!readOnlyReason;

  // Identity, not role. `author_realm` is what separates the two id
  // spaces; `user_id` is absent rather than null on a member's comment and
  // is never the thing to compare.
  const isMine = (comment: CommentReturn): boolean =>
    comment.author_realm === 'member' &&
    !!member?.member_id &&
    comment.author_id === member.member_id;

  const nameFor = (comment: CommentReturn): string =>
    isMine(comment)
      ? 'You'
      : comment.author_realm === 'member'
        ? otherMemberLabel
        : MANAGEMENT_AUTHOR;

  const post = async (text: string, parentId?: string) => {
    setBusy(true);
    setWriteError(null);
    try {
      await source.post(text, parentId);
      setContent('');
      setReplyContent('');
      setReplyTo(null);
      reload();
    } catch (err) {
      console.error('Error posting comment:', err);
      // The reply is kept so a failure does not cost what was written.
      setWriteError(billingErrorMessage(err, "Couldn't post your reply."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (comment: CommentReturn) => {
    if (!window.confirm('Delete your comment?')) return;
    setBusy(true);
    setWriteError(null);
    try {
      await source.remove(comment.id);
      reload();
    } catch (err) {
      console.error('Error deleting comment:', err);
      setWriteError(billingErrorMessage(err, "Couldn't delete your comment."));
    } finally {
      setBusy(false);
    }
  };

  const renderNode = (node: CommentNode) => {
    const { comment } = node;
    const mine = isMine(comment);

    return (
      <li
        key={comment.id}
        className={
          node.depth > 0
            ? 'ml-6 border-l-2 border-zinc-200 pl-4 dark:border-zinc-600'
            : 'border-l-2 border-zinc-200 pl-4 dark:border-zinc-600'
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            {nameFor(comment)}
          </span>
          <time
            dateTime={comment.created_at}
            title={formatTimestamp(comment.created_at)}
            className="text-xs text-gray-500 dark:text-gray-300"
          >
            {formatRelativeTime(comment.created_at)}
          </time>
          {comment.is_edited && (
            <span className="text-xs text-gray-500 dark:text-gray-300">(edited)</span>
          )}
        </div>

        {node.inReplyTo && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-300">
            In reply to {nameFor(node.inReplyTo)}
          </p>
        )}

        <div className="mt-1">
          {comment.is_deleted ? (
            <Text className="italic">This comment was deleted.</Text>
          ) : (
            <Text className="whitespace-pre-wrap break-words">{comment.content}</Text>
          )}
        </div>

        {!comment.is_deleted && (comment.attachments?.length ?? 0) > 0 && (
          <div className="mt-2 flex flex-wrap items-start gap-2">
            {comment.attachments!.map((attachment) => (
              <MemberAttachmentThumb key={attachment.id} attachment={attachment} />
            ))}
          </div>
        )}

        {!comment.is_deleted && !readOnly && (
          <div className="mt-2 flex flex-wrap gap-2">
            {node.depth === 0 && (
              <Button
                plain
                onClick={() => {
                  setReplyContent('');
                  setReplyTo(comment.id);
                }}
              >
                Reply
              </Button>
            )}
            {mine && (
              <Button plain onClick={() => remove(comment)} disabled={busy}>
                Delete
              </Button>
            )}
          </div>
        )}

        {replyTo === comment.id && (
          <div className="mt-3">
            <Textarea
              rows={3}
              value={replyContent}
              disabled={busy}
              autoFocus
              onChange={(e) => setReplyContent(e.target.value)}
            />
            <div className="mt-2 flex gap-2">
              <Button
                disabled={busy || replyContent.trim().length === 0}
                onClick={() => post(replyContent.trim(), comment.id)}
              >
                {busy ? 'Posting…' : 'Post reply'}
              </Button>
              <Button plain onClick={() => setReplyTo(null)} disabled={busy}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {node.replies.length > 0 && (
          <ul className="mt-4 space-y-4">{node.replies.map(renderNode)}</ul>
        )}
      </li>
    );
  };

  return (
    <div className="bg-slate-100 dark:bg-gray-600 p-6 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">{title}</h3>

      <ErrorAlert className="mb-3">{error}</ErrorAlert>
      <ErrorAlert className="mb-3" onDismiss={() => setWriteError(null)}>
        {writeError}
      </ErrorAlert>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {tree.length === 0 ? (
            <Text>{emptyText}</Text>
          ) : (
            <ul className="space-y-6">{tree.map(renderNode)}</ul>
          )}

          {readOnly ? (
            <Text className="mt-6 text-sm">{readOnlyReason}</Text>
          ) : (
            <div className="mt-6">
              <Textarea
                rows={3}
                value={content}
                disabled={busy}
                placeholder="Write a message…"
                onChange={(e) => setContent(e.target.value)}
              />
              <div className="mt-2">
                <Button
                  disabled={busy || content.trim().length === 0}
                  onClick={() => post(content.trim())}
                >
                  {busy ? 'Posting…' : sendLabel}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default MemberCommentThread;
